import React from 'react';
import { Box, Stack, Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Typography } from '@chr/eds-react';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { JobWithAddedIds } from '@/common/internalModels';
import { Measurements } from '@/common/constants/measurements';
import { formatNumber, formatWeight } from '@/common/utilities/formatNumber';

const columns: GridColDef[] = [
  { field: 'sku', headerName: 'SKU/Part Number', flex: 1, minWidth: 150 },
  { field: 'unitQuantity', headerName: 'Quantity', type: 'number', flex: 1, minWidth: 150 },
  { field: 'packaging', headerName: 'Packaging', flex: 1, minWidth: 150 },
  { field: 'totalWeight', headerName: 'Total Weight', flex: 1, minWidth: 150 },
  { field: 'totalPallets', headerName: 'Pallets', type: 'number', flex: 1, minWidth: 150 },
];

interface OrderItemDetailsProps {
  shipment: JobWithAddedIds;
}

export const OrderItemDetails = (props: OrderItemDetailsProps) => {
  const translate = useTranslation();
  const summary = props.shipment.items.reduce((acc, item) => {
    acc.totalItems += 1;
    acc.totalQuantity += item.unitQuantity ?? 0;
    acc.totalWeight += item.totalWeight.value ?? 0;
    acc.totalPallets += item.totalPallets ?? 0;
    return acc;
  }, { totalItems: 0, totalQuantity: 0, totalWeight: 0, totalPallets: 0 });

  const items = props.shipment.items.map(item => ({
    id: item.id,
    sku: item.sku,
    packaging: item.packaging.value,
    unitQuantity: formatNumber(item.unitQuantity, 3),
    totalWeight: formatWeight(item.totalWeight.value, Measurements.getWeightUnitLabel(item.totalWeight?.unitRDN)),
    totalPallets: formatNumber(item.totalPallets, 3),
  }))

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h2">{translate('Item Details')}</Typography>
      <Grid container spacing={2} mb={2} mt={2} ml={2} mr={2}>
        <Grid item xs={6} sm={3}>
          <Stack spacing={1}>
            <Typography variant="body1">{translate('Number of Items')}</Typography>
            <Typography variant="subtitle2">{summary.totalItems}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stack spacing={1}> 
            <Typography variant="body1">{translate('Quantity')}</Typography>
            <Typography variant="subtitle2">{formatNumber(summary.totalQuantity, 3)}</Typography>
          </Stack>  
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stack spacing={1}>           
            <Typography variant="body1">{translate('Total Weight')}</Typography>
            <Typography variant="subtitle2">{formatWeight(summary.totalWeight, Measurements.getWeightUnitLabel(props.shipment.items[0]?.totalWeight?.unitRDN))}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stack spacing={1}>   
            <Typography variant="body1">{translate('Pallets')}</Typography>
            <Typography variant="subtitle2">{formatNumber(summary.totalPallets, 3)}</Typography>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ height: 400 }} mt={4}>
        <DataGrid
          columns={columns}
          rows={items}
          pagination
          initialState={{
            pagination: {
              paginationModel: { pageSize: 5, page: 0 },
            },
          }}
          pageSizeOptions={[5, 10]}
          sx={{
            border: 1,
            borderColor: 'divider',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '2px solid rgba(224, 224, 224, 1)',
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '12px !important',
              fontWeight: '400 !important',
            },
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: '#f9f9f9',
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#ffffff',
            },
          }}
        />
      </Box>
    </Box>
  );
};